import { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { THEME } from "@/config/theme";

export interface PanelItem {
  key: string;
  label: string;
  preview: React.ReactNode;
  /** Synonyms/alternate names matched by search (e.g. "pc" → Computer). */
  keywords?: string[];
}

interface Props {
  items: PanelItem[];
  defaultKeys: string[];
  recentKeys: string[];
  onSelect: (key: string) => void;
  onClose: () => void;
}

// Desktop: anchored just under the toolbar button (absolute, left-aligned).
// Small screens (especially landscape phones): a left-anchored 320px panel runs
// off the right edge and can exceed the short viewport height — so pin it to the
// viewport, center it, clamp width to the screen, and allow vertical scroll.
const PANEL_CLASS =
  "z-50 flex flex-col gap-2 rounded-xl p-2 shadow-2xl shadow-black/50 " +
  "fixed left-1/2 top-14 -translate-x-1/2 w-[calc(100vw-1rem)] max-w-[15rem] max-h-[calc(100dvh-4.5rem)] overflow-y-auto " +
  "sm:absolute sm:left-0 sm:top-full sm:mt-1.5 sm:translate-x-0 sm:w-80 sm:max-w-none sm:max-h-none sm:overflow-visible";

function ItemGrid({ items, onSelect, onClose }: { items: PanelItem[]; onSelect: (k: string) => void; onClose: () => void }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
      {items.map(({ key, label, preview }) => (
        <button
          key={key}
          onClick={() => { onSelect(key); onClose(); }}
          title={label}
          className={`flex flex-col items-center gap-1 p-1.5 rounded-lg ${THEME.popup.itemHover} transition-colors group`}
        >
          <div className={`${THEME.popup.muted} group-hover:text-zinc-100 transition-colors`}>
            {preview}
          </div>
          <span className={`text-[10px] ${THEME.popup.muted} group-hover:text-zinc-100 leading-tight w-full text-center break-words`}>
            {label}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function ElementSearchPanel({ items, defaultKeys, recentKeys, onSelect, onClose }: Props) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const byKey = new Map(items.map((i) => [i.key, i]));

  if (query.trim()) {
    const q = query.toLowerCase();
    const filtered = items.filter((i) =>
      i.label.toLowerCase().includes(q) ||
      i.key.toLowerCase().includes(q) ||
      i.keywords?.some((k) => k.toLowerCase().includes(q)),
    );
    return (
      <div className={`${PANEL_CLASS} ${THEME.popup.container}`}>
        <SearchInput inputRef={inputRef} value={query} onChange={setQuery} />
        {filtered.length === 0
          ? <p className={`text-[12px] ${THEME.popup.muted} px-2 py-2`}>No results</p>
          : <ItemGrid items={filtered} onSelect={onSelect} onClose={onClose} />
        }
      </div>
    );
  }

  const recentItems  = recentKeys.map((k) => byKey.get(k)).filter(Boolean) as PanelItem[];
  const defaultItems = defaultKeys.map((k) => byKey.get(k)).filter(Boolean) as PanelItem[];

  return (
    <div className={`${PANEL_CLASS} ${THEME.popup.container}`}>
      <SearchInput inputRef={inputRef} value={query} onChange={setQuery} />

      {recentItems.length > 0 && (
        <section>
          <p className={`text-[10px] font-medium ${THEME.popup.sectionLabel} px-1 mb-1 uppercase tracking-wide`}>Recent</p>
          <ItemGrid items={recentItems} onSelect={onSelect} onClose={onClose} />
        </section>
      )}

      <section>
        <p className="text-[10px] font-medium text-zinc-500 px-1 mb-1 uppercase tracking-wide font-mono">
          {recentItems.length > 0 ? "Defaults" : "All"}
        </p>
        <ItemGrid items={defaultItems} onSelect={onSelect} onClose={onClose} />
      </section>
    </div>
  );
}

function SearchInput({
  inputRef, value, onChange,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${THEME.popup.input}`}>
      <Search size={12} className={`${THEME.popup.muted} shrink-0`} />
      <input
        ref={inputRef as React.Ref<HTMLInputElement>}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        className={`flex-1 bg-transparent text-[13px] ${THEME.popup.inputText} outline-none`}
      />
    </div>
  );
}
